import { Field, Form } from 'formik';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(40px); }
  to { opacity: 1; transform: translateY(0); }
`;

/* ==========================================================
   HERO
   ========================================================== */

export const PageHero = styled.section<{ $bgImage?: string }>`
  min-height: 70vh;
  background-image:
    linear-gradient(180deg,
      rgba(10, 22, 40, 0.15) 0%,
      rgba(10, 22, 40, 0.4) 50%,
      rgba(10, 22, 40, 0.92) 85%,
      var(--bg-primary) 100%),
    url(${({ $bgImage }) => $bgImage || ''});
  background-size: cover;
  background-position: center 40%;
  background-attachment: fixed;
  display: flex;
  align-items: flex-end;
  padding: 0 1.5rem 4rem;

  @media (max-width: 768px) {
    background-attachment: scroll;
    min-height: 55vh;
    padding-bottom: 3rem;
    background-position: center 30%;
  }
`;

export const HeroInner = styled.div`
  max-width: var(--container-max, 1200px);
  margin: 0 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
  animation: ${fadeInUp} 0.8s ease-out;

  @media (max-width: 768px) {
    align-items: flex-start;
    text-align: left;
  }
`;

export const HeroEyebrow = styled.div`
  display: inline-block;
  font-family: var(--font-heading);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: var(--accent-teal);
  background: rgba(10, 22, 40, 0.55);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  border: 1px solid rgba(0, 201, 177, 0.3);
  padding: 0.5rem 1.4rem;
  border-radius: 100px;
  margin-bottom: 1rem;
`;

export const HeroTitle = styled.h1`
  font-family: var(--font-heading);
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 800;
  color: var(--text-primary);
  margin-bottom: 1rem;
  text-shadow: 0 4px 30px rgba(0, 0, 0, 0.5);

  span {
    background: var(--gradient-gold);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

export const HeroSubtitle = styled.p`
  font-size: clamp(1rem, 1.6vw, 1.15rem);
  color: var(--text-primary);
  max-width: 560px;
  line-height: 1.8;
  background: rgba(10, 22, 40, 0.5);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 1rem 1.5rem;
  border-radius: var(--border-radius-lg);
`;

/* ==========================================================
   CONTENT LAYOUT
   ========================================================== */

export const ContentContainer = styled.div`
  max-width: var(--container-max, 1200px);
  margin: 0 auto;
  padding: 3rem 1.5rem 4rem;
`;

export const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2.5rem;

  @media (min-width: 992px) {
    grid-template-columns: 1.2fr 0.8fr;
  }
`;

/* ==========================================================
   FORM CARD (left column)
   ========================================================== */

export const FormCard = styled.div`
  background: linear-gradient(
    135deg,
    rgba(15, 25, 45, 0.95) 0%,
    rgba(10, 22, 40, 0.98) 100%
  );
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

export const FormTitle = styled.h2`
  font-family: var(--font-heading);
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 0.5rem;
`;

export const FormSubtitle = styled.p`
  color: var(--text-muted);
  font-size: 0.95rem;
  margin: 0 0 1.8rem;
  line-height: 1.5;
`;

export const ContactForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

export const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.2rem;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const Label = styled.label`
  font-family: var(--font-heading);
  font-weight: 600;
  font-size: 0.85rem;
  margin-bottom: 0.45rem;
  color: var(--text-secondary);
  letter-spacing: 0.3px;
`;

export const StyledField = styled(Field)`
  padding: 0.75rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--border-radius);
  font-size: 0.95rem;
  font-family: var(--font-body);
  color: var(--text-primary);
  background-color: rgba(10, 18, 35, 0.8);
  transition: all var(--transition-fast);

  &::placeholder {
    color: var(--text-muted);
    opacity: 0.6;
  }

  &:focus {
    outline: none;
    border-color: var(--accent-teal);
    box-shadow: 0 0 0 3px rgba(0, 201, 177, 0.12);
    background-color: rgba(10, 18, 35, 1);
  }
`;

export const StyledSelect = styled(Field)`
  padding: 0.75rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--border-radius);
  font-size: 0.95rem;
  font-family: var(--font-body);
  color: var(--text-primary);
  background-color: rgba(10, 18, 35, 0.8);
  transition: all var(--transition-fast);
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--accent-teal);
    box-shadow: 0 0 0 3px rgba(0, 201, 177, 0.12);
  }

  option {
    background-color: var(--bg-primary);
    color: var(--text-primary);
  }
`;

export const ErrorText = styled.div`
  color: var(--accent-coral);
  font-size: 0.8rem;
  margin-top: 0.35rem;
`;

export const SubmitButton = styled.button`
  background: var(--gradient-gold);
  color: var(--text-inverse);
  border: none;
  border-radius: var(--border-radius);
  padding: 0.9rem 1.5rem;
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: 1rem;
  letter-spacing: 0.5px;
  cursor: pointer;
  margin-top: 0.5rem;
  transition: all var(--transition-base);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-shadow: var(--shadow-glow-gold);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(244, 170, 0, 0.35);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

/* ==========================================================
   SUCCESS / ERROR STATES
   ========================================================== */

export const SuccessCard = styled.div`
  background: linear-gradient(
    135deg,
    rgba(0, 201, 177, 0.06) 0%,
    rgba(0, 201, 177, 0.02) 100%
  );
  border: 1px solid rgba(0, 201, 177, 0.25);
  border-radius: var(--border-radius-lg);
  padding: 2.5rem 2rem;
  text-align: center;
`;

export const SuccessIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(0, 201, 177, 0.1);
  border: 2px solid var(--accent-teal);
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto 1.2rem;

  svg {
    font-size: 1.8rem;
    color: var(--accent-teal);
  }
`;

export const SuccessTitle = styled.h3`
  font-family: var(--font-heading);
  color: var(--text-primary);
  font-size: 1.3rem;
  margin: 0 0 0.6rem;
`;

export const SuccessText = styled.p`
  color: var(--text-secondary);
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0;
`;

export const ErrorBanner = styled.div`
  background: rgba(192, 57, 43, 0.08);
  border: 1px solid rgba(192, 57, 43, 0.3);
  border-radius: var(--border-radius);
  padding: 0.75rem 1rem;
  font-size: 0.88rem;
  color: var(--accent-coral);
  line-height: 1.5;
`;

/* ==========================================================
   INFO PANEL (right column)
   ========================================================== */

export const InfoPanel = styled.div`
  background: linear-gradient(
    135deg,
    rgba(15, 25, 45, 0.95) 0%,
    rgba(10, 22, 40, 0.98) 100%
  );
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  padding: 2rem;
  height: fit-content;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

export const InfoTitle = styled.h3`
  font-family: var(--font-heading);
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 1.5rem;
`;

export const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 1.3rem;
`;

export const InfoIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(0, 201, 177, 0.08);
  border: 1px solid rgba(0, 201, 177, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    color: var(--accent-teal);
    font-size: 0.9rem;
  }
`;

export const InfoContent = styled.div`
  display: flex;
  flex-direction: column;
`;

export const InfoLabel = styled.span`
  font-family: var(--font-heading);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 0.2rem;
`;

export const InfoValue = styled.span`
  color: var(--text-primary);
  font-size: 0.95rem;
  line-height: 1.4;

  a {
    color: var(--text-primary);
    text-decoration: none;
    transition: color var(--transition-fast);

    &:hover {
      color: var(--accent-teal);
    }
  }
`;

export const Divider = styled.hr`
  border: none;
  border-top: 1px solid var(--border-color);
  margin: 1.5rem 0;
`;

export const BookingCta = styled.p`
  color: var(--text-muted);
  font-size: 0.88rem;
  line-height: 1.5;
  margin: 0;
`;

export const BookingCtaLink = styled(Link)`
  color: var(--accent-teal);
  font-weight: 600;
  text-decoration: none;
  transition: color var(--transition-fast);

  &:hover {
    color: var(--text-primary);
    text-decoration: underline;
  }
`;
